import _ from 'lodash';

import { AUDIENCE_CATEGORY_HIERARCHY } from 'states/resources/audiences/business-logic';

// Function to transform AUDIENCE_CATEGORY_HIERARCHY by removing Custom Audiences from Pelmorex Data sections
function transformHierarchyForSegmentPicker(hierarchy) {
    return hierarchy.map(group => {
        // Check if this is the Pelmorex Data category
        if (group.value === 'pelmorex_data' && group.sections) {
            return {
                ...group,
                sections: group.sections.filter(section => {
                    // Remove Custom Audiences section from Pelmorex Data
                    return section.value !== 'custom_audiences';
                }),
            };
        }

        // Check if this is the Custom Audiences category to add section header
        if (group.value === 'custom_audiences') {
            return {
                label: group.label,
                value: group.value,
                sections: [
                    { label: group.label, value: group.value, categories: group.categories },
                ],
            };
        }
        return group;
    });
}

export const TORSTAR_ID = '60240676c48e547c2b3d114d';

const AUDIENCE_CATEGORIES_RESTRUCTURED = [
    {
        value: 'all',
        label: 'all',
    },
    ...transformHierarchyForSegmentPicker(AUDIENCE_CATEGORY_HIERARCHY),
];

export const getAudienceCategories = () => {
    return AUDIENCE_CATEGORIES_RESTRUCTURED;
};

export function getSegments({
    allSegments,
    sourceType,
    tab,
    restrictedSegments,
    shouldHideFlexSegments,
    platforms,
    platform,
    keywords,
    isCrossPlatformCampaign,
    orgId,
}) {
    const audienceCategories = getAudienceCategories();

    let segmentTypes;

    // Handle new tab structure for restructured categories
    const tabMapping = {
        pelmorex_data: ['pelmorex_data'],
        custom_audiences: ['custom_audiences'],
        partnerships: ['partnerships'],
        advertiser_data: ['advertiser_data'],
    };

    segmentTypes = _(audienceCategories)
        .filter(category => category && category.value !== 'all')
        .filter(category => {
            if (tab === 'all') {
                return true;
            }

            const allowedCategories = tabMapping[tab] || [tab];
            return _.includes(allowedCategories, category.value);
        })
        .map(category => {
            // Handle categories with sections (new hierarchical structure)
            if (category.sections) {
                return _(category.sections)
                    .map(section => section.categories || [])
                    .flatten()
                    .value();
            }
            // Handle categories with direct categories
            return category.categories || [];
        })
        .flatten()
        .filter(subCategory => subCategory && subCategory.valuesForSegmentPicker)
        .filter(subCategory => {
            if (sourceType === 'all') {
                return true;
            }

            return _.isEqual(sourceType, subCategory.valuesForSegmentPicker);
        })
        .filter(subCategory => {
            if (!subCategory || !subCategory.shouldHide) {
                return true;
            }
            return !subCategory.shouldHide({
                restrictedSegments,
                shouldHideFlexSegments,
                platforms,
                platform,
                isCrossPlatformCampaign,
                orgId,
            });
        })
        .map(subCategory => subCategory.valuesForSegmentPicker)
        .flatten()
        .value();

    return _(allSegments)
        .filter(segment => !segment.deprecated)
        .filter(segment => _.includes(segmentTypes, segment.source_type))
        .filter(segment => {
            if (!keywords) {
                return true;
            }
            const compareKeywords = _.toLower(keywords);
            return (
                _.includes(_.toLower(segment.id), compareKeywords) ||
                _.includes(_.toLower(segment.name), compareKeywords) ||
                _.includes(_.toLower(segment.description), compareKeywords)
            );
        })
        .value();
}

export const SOURCES = {
    devices: 'uniqueDevices',
    cookies: 'uniqueCookies',
};

const getSourceFromSegmentSupport = ({ segment }) => {
    if (_.includes(segment.supports, 'inapp') && !_.includes(segment.supports, 'mweb')) {
        return SOURCES.devices;
    } else if (!_.includes(segment.supports, 'inapp') && _.includes(segment.supports, 'mweb')) {
        return SOURCES.cookies;
    } else if (_.includes(segment.supports, 'inapp') && _.includes(segment.supports, 'mweb')) {
        return null;
    }
};

export const SUPPORTED_AUDIENCES_PLATFORMS_PERCENTAGES = {
    pds_brand: {
        getDominantSource: () => {
            return SOURCES.devices;
        },
        getSupportedPlatforms: () => {
            return ['inapp', 'mweb', 'desktop'];
        },
    },
    pds_category: {
        getDominantSource: () => {
            return SOURCES.devices;
        },
        getSupportedPlatforms: () => {
            return ['inapp', 'mweb', 'desktop'];
        },
    },
    pds_behavioural: {
        getDominantSource: () => {
            return SOURCES.devices;
        },
        getSupportedPlatforms: () => {
            return ['inapp', 'mweb', 'desktop'];
        },
    },
    pds_seasonal: {
        getDominantSource: () => {
            return SOURCES.devices;
        },
        getSupportedPlatforms: () => {
            return ['inapp', 'mweb', 'desktop'];
        },
    },
    flex_standard_brand: {
        getDominantSource: () => {
            return SOURCES.devices;
        },
        getSupportedPlatforms: () => {
            return ['inapp'];
        },
    },
    flex_location_category: {
        getDominantSource: () => {
            return SOURCES.devices;
        },
        getSupportedPlatforms: () => {
            return ['inapp'];
        },
    },
    pds_intent: {
        getDominantSource: () => {
            return SOURCES.cookies;
        },
        getSupportedPlatforms: () => {
            return ['inapp', 'mweb', 'desktop'];
        },
    },
    pds_manifold: {
        getDominantSource: () => {
            return SOURCES.devices;
        },
        getSupportedPlatforms: () => {
            return ['inapp', 'mweb', 'desktop'];
        },
    },
    pds_environics: {
        getDominantSource: () => {
            return SOURCES.devices;
        },
        getSupportedPlatforms: () => {
            return ['inapp', 'mweb', 'desktop'];
        },
    },
    pds_mastercard: {
        getDominantSource: () => {
            return SOURCES.devices;
        },
        getSupportedPlatforms: () => {
            return ['inapp', 'mweb', 'desktop'];
        },
    },
    pds_declared_age: {
        getDominantSource: () => {
            return SOURCES.devices;
        },
        getSupportedPlatforms: () => {
            return ['inapp', 'mweb', 'desktop'];
        },
    },
    pds_custom_defined: {
        getDominantSource: () => {
            return SOURCES.devices;
        },
        getSupportedPlatforms: () => {
            return ['inapp', 'mweb', 'desktop'];
        },
    },
    pds_custom_developed: {
        getDominantSource: () => {
            return SOURCES.devices;
        },
        getSupportedPlatforms: () => {
            return ['inapp', 'mweb', 'desktop'];
        },
    },
    flex_custom_brand: {
        getDominantSource: () => {
            return SOURCES.devices;
        },
        getSupportedPlatforms: () => {
            return ['inapp'];
        },
    },
    ad: {
        getDominantSource: ({ segment }) => {
            return getSourceFromSegmentSupport({ segment });
        },
        getSupportedPlatforms: ({ segment }) => {
            return segment.supports;
        },
    },
    campaign: {
        getDominantSource: ({ segment }) => {
            return getSourceFromSegmentSupport({ segment });
        },
        getSupportedPlatforms: ({ segment }) => {
            return segment.supports;
        },
    },
    pixel: {
        getDominantSource: () => {
            return SOURCES.cookies;
        },
        getSupportedPlatforms: () => {
            return ['mweb'];
        },
    },
    upload: {
        getDominantSource: ({ segment, isInternalUser }) => {
            if (!isInternalUser) {
                return SOURCES.devices;
            }
            return getSourceFromSegmentSupport({ segment });
        },
        getSupportedPlatforms: ({ segment }) => {
            return segment.supports;
        },
    },
    adobe: {
        getDominantSource: ({ segment }) => {
            return getSourceFromSegmentSupport({ segment });
        },
        getSupportedPlatforms: ({ segment }) => {
            return segment.supports;
        },
    },
    permutive: {
        getDominantSource: ({ segment }) => {
            return getSourceFromSegmentSupport({ segment });
        },
        getSupportedPlatforms: ({ segment }) => {
            return segment.supports;
        },
    },
    pds_mweb: {
        getDominantSource: () => {
            return SOURCES.cookies;
        },
        getSupportedPlatforms: () => {
            return ['mweb'];
        },
    },
};
